import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { LogOut, AlertTriangle } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { logoutSession } from "@/lib/userProfile";

/**
 * Logout button + confirmation modal — always available from the chrome
 * (Header / Footer / in-game controls). Clicking it asks "Are you sure?"
 * and, on confirm, wipes all session storage (name/school/nationality/
 * avatar/language/intro_shown) and reloads the home page.
 *
 * When the user is mid-game we surface an extra red warning informing that
 * the running match will count as a loss. We auto-detect this from the URL
 * (any route under `/jogo/`, `/jogar/`, `/play/`, `/eliminatoria/play/`)
 * but the parent can force it via the `inGame` prop.
 */
export default function LogoutButton({ variant = "pill", className = "", showLabel = true, inGame = null }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const inGameAuto = /\/(jogo|jogar|play|partida|duelo|eliminatoria\/play)\b/i.test(pathname);
  const willLose = inGame === null ? inGameAuto : Boolean(inGame);

  const base = "inline-flex items-center gap-2 font-bold transition";
  const styles = {
    pill:   `${base} bg-slate-700 hover:bg-slate-800 text-white rounded-full px-4 py-1.5 text-sm border-b-2 border-slate-900 active:translate-y-[1px] active:border-b-0`,
    inline: `${base} bg-slate-600 hover:bg-slate-700 text-white rounded-full shadow-md border-b-2 border-slate-800 active:translate-y-[1px] active:border-b-0 px-4 py-1.5 text-sm`,
    link:   `${base} text-slate-500 hover:text-rose-600 underline-offset-4 hover:underline text-xs`,
    icon:   `${base} bg-slate-700 hover:bg-slate-800 text-white rounded-full p-2`,
  };

  return (
    <>
      <button
        data-testid="btn-logout"
        title={t("logout")}
        aria-label={t("logout")}
        onClick={() => setOpen(true)}
        className={`${styles[variant] || styles.pill} ${className}`}
      >
        <LogOut className="w-4 h-4" />
        {showLabel && <span>{t("logout")}</span>}
      </button>

      {open && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4" data-testid="logout-modal" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
            <div className="text-5xl mb-3" aria-hidden>👋</div>
            <h2 className="text-xl md:text-2xl font-[Fredoka] font-bold text-slate-900 mb-3">
              {t("logout_confirm")}
            </h2>
            {willLose && (
              <div className="mb-4 mx-auto max-w-xs rounded-xl bg-rose-50 border-2 border-rose-300 px-3 py-2 flex items-center gap-2" data-testid="logout-warn-loss">
                <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0" />
                <p className="text-sm font-bold text-rose-700 leading-tight text-left">{t("logout_warn_loss")}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <button
                data-testid="btn-logout-no"
                onClick={() => setOpen(false)}
                className="px-4 py-3 bg-slate-200 hover:bg-slate-300 text-slate-900 rounded-full font-bold border-b-2 border-slate-400 active:translate-y-[1px] active:border-b-0"
              >
                {t("no").toUpperCase()}
              </button>
              <button
                data-testid="btn-logout-yes"
                onClick={() => { setOpen(false); logoutSession(); }}
                className="px-4 py-3 bg-rose-500 hover:bg-rose-600 text-white rounded-full font-bold border-b-2 border-rose-700 active:translate-y-[1px] active:border-b-0"
              >
                {t("yes").toUpperCase()}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
